"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Accordion } from "@/components/Accordion";
import { CmsText } from "@/components/CmsText";

type AddOn = { title: string; body: string; image: string; imageAlt: string };

// Add-on experiences as an image-left / accordion-right band. The left photo
// cross-fades to match whichever add-on is open (Accordion in controlled mode).
// When `c` is supplied, titles/bodies/images become CMS-editable in place
// (keys: <prefix>.<i>.title / .body / .image); the change-image button acts on
// whichever add-on is currently open.
export function AddOnsAccordion({
  items,
  c = {},
  keyPrefix = "addon",
}: {
  items: AddOn[];
  c?: Record<string, string>;
  keyPrefix?: string;
}) {
  const [open, setOpen] = useState<number | null>(0);
  const idx = open ?? 0;
  const active = items[idx] ?? items[0];
  const activeSrc = c[`${keyPrefix}.${idx}.image`]?.trim() || active.image;
  const activeAlt = c[`${keyPrefix}.${idx}.image.alt`]?.trim() || active.imageAlt;

  return (
    <div className="mt-10 grid items-stretch gap-12 md:mt-14 md:grid-cols-2 md:gap-16">
      {/* left — image synced to the open item */}
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl ring-1 ring-brand-line md:aspect-auto md:h-full md:min-h-[24rem]">
        <AnimatePresence initial={false}>
          <motion.img
            key={activeSrc}
            src={activeSrc}
            alt={activeAlt}
            data-cms-img={`${keyPrefix}.${idx}.image`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 h-full w-full object-cover [filter:contrast(1.03)_saturate(1.04)]"
          />
        </AnimatePresence>
      </div>

      {/* right — the add-ons as dropdown accordions */}
      <div>
        <Accordion
          items={items.map((a, i) => ({
            title: <CmsText c={c} k={`${keyPrefix}.${i}.title`}>{a.title}</CmsText>,
            body: <CmsText c={c} k={`${keyPrefix}.${i}.body`} as="div">{a.body}</CmsText>,
          }))}
          open={open}
          onOpenChange={setOpen}
        />
      </div>
    </div>
  );
}
